import { BrowserModule } from '@angular/platform-browser'
import { NgModule } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MomentModule } from 'ngx-moment'
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { LoginComponent } from './login/login.component'
import { CallsComponent } from './calls/calls.component'
import { AuthManager } from './shared/auth/AuthManager'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http'
import { AuthInterceptor } from './shared/auth/AuthInterceptor'
import { AuthGuard } from './shared/auth/AuthGuard'
import { HmRestrictionGuard } from './shared/auth/HmRestrictionGuard'

import {
   AuthApiClient,
   CallsApiClient,
   EmployeeTypeMapApiClient,
   FilesApiClient,
   EscheatmentApiClient,
   PaymentTypeGroupMapClient,
   ProductsClient,
   DoctorScheduleClient,
   FaxDataClient,
   AdminProductsClient,
   PricingManagerClient,
   GrowthToolkitClient,
   DVMPlanClient,
   EntraSyncClient,
   LeaderboardClient,
   CreateZohoTicketForAppsClient,
   PracticesClient,
   MessagingClient,
   RoleManagerClient,
} from './shared/services/ppv.api'
import { CampaignSelectorComponent } from './campaign-selector/campaign-selector.component'
import { CampaignItemComponent } from './campaign-item/campaign-item.component'
import { ScrollingModule } from '@angular/cdk/scrolling'
import { LandingComponent } from './landing/landing.component'
import { NgxFileDropModule } from 'ngx-file-drop'
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { ToastrModule } from 'ngx-toastr'
import { BlockUIModule } from 'ng-block-ui'
import { TableModule } from 'primeng/table'
import { DropdownModule } from 'primeng/dropdown'
import { TabViewModule } from 'primeng/tabview'
import { MultiSelectModule } from 'primeng/multiselect'
import { InputTextModule } from 'primeng/inputtext'
import { InputNumberModule } from 'primeng/inputnumber'
import { MessageModule } from 'primeng/message'
import { ListboxModule } from 'primeng/listbox'
import { CalendarModule } from 'primeng/calendar'
import { BreadcrumbModule } from 'primeng/breadcrumb'
import { SelectModule } from 'primeng/select'
import { CheckboxModule } from 'primeng/checkbox'
import { PopoverModule } from 'primeng/popover'
import { RadioButtonModule } from 'primeng/radiobutton'
import { MessagesModule } from 'primeng/messages'
import { EmployeeTypeMapComponent } from './employee-type-map/employee-type-map.component'
import { FileUploadModule } from 'primeng/fileupload'
import { SelectButtonModule } from 'primeng/selectbutton'
import { EscheatmentComponent } from './escheatment/escheatment.component'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { ConfirmationService } from 'primeng/api'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { DividerModule } from 'primeng/divider'
import { DialogModule } from 'primeng/dialog'
import { PaymentGroupMapComponent } from './payment-group-map/payment-group-map.component'
import { ProductsModule } from './products/products.module'
import { provideEnvironmentNgxMask } from 'ngx-mask'
import { SharedModule } from './shared/shared.module'
import { DoctorScheduleComponent } from './doctor-schedule/doctor-schedule.component'
import { TagModule } from 'primeng/tag'
import { TabMenuModule } from 'primeng/tabmenu'
import { ButtonModule } from 'primeng/button'
import { SplitButtonModule } from 'primeng/splitbutton'
import { FeedbackService } from './shared/services/feedback'
import { VetcorPricingModule } from './vetcor-pricing/vetcor-pricing.module'
import { DvmToolModule } from './dvm-tool/dvm-tool.module'
import { TooltipModule } from 'primeng/tooltip'
import { InputIconModule } from 'primeng/inputicon'
import { IconFieldModule } from 'primeng/iconfield'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { providePrimeNG } from 'primeng/config'
import Lara from '@primeng/themes/lara/'
import { EntraSyncModule } from './entra-sync/entra-sync.module'
import { GrowthToolkitModule } from './growth-toolkit/growth-toolkit.module'
import { LeaderboardModule } from './leaderboard/leaderboard.module'
import { MentoringAgreementModule } from './mentoring-agreement/mentoring-agreement.module'
import { MessagingAppModule } from './messaging/messaging-app.module'
import { MsalModule, MsalService, MSAL_INSTANCE } from '@azure/msal-angular'
import { PublicClientApplication, IPublicClientApplication } from '@azure/msal-browser'
import { RulesEngineInterceptor } from './shared/rulesEngine/RulesEngineInterceptor'
import { RoleManagerModule } from './role-manager/role-manager.module'
import { LeaderToolkitModule } from './leader-toolkit/leader-toolkit.module'

export function MSALInstanceFactory(): IPublicClientApplication {
   const instance = new PublicClientApplication({
      auth: {
         clientId: 'e9339c45-00ab-46ac-8a0b-97ad144e1d98', //Meetings Scheduler App
         authority: 'https://login.microsoftonline.com/523b8b04-c730-4da9-a587-46cf3a2eb728',
         redirectUri: window.location.origin,
      },
   })

   return instance
}

@NgModule({
   declarations: [
      AppComponent,
      LoginComponent,
      CallsComponent,
      CampaignSelectorComponent,
      CampaignItemComponent,
      LandingComponent,
      EmployeeTypeMapComponent,
      EscheatmentComponent,
      PaymentGroupMapComponent,
      DoctorScheduleComponent,
   ],
   bootstrap: [AppComponent],
   imports: [
      BrowserModule,
      AppRoutingModule,
      FormsModule,
      MomentModule,
      NgIdleKeepaliveModule.forRoot(),
      ScrollingModule,
      NgxFileDropModule,
      SweetAlert2Module.forRoot(),
      BrowserAnimationsModule,
      ToastrModule.forRoot(),
      BlockUIModule.forRoot(),
      TableModule,
      DropdownModule,
      TabViewModule,
      MessagesModule,
      MultiSelectModule,
      IconFieldModule,
      InputIconModule,
      InputTextModule,
      InputNumberModule,
      MessageModule,
      ListboxModule,
      CalendarModule,
      BreadcrumbModule,
      SelectModule,
      TooltipModule,
      CheckboxModule,
      PopoverModule,
      RadioButtonModule,
      ProductsModule,
      FileUploadModule,
      SelectButtonModule,
      SplitButtonModule,
      SharedModule,
      ConfirmDialogModule,
      AutoCompleteModule,
      ProgressSpinnerModule,
      DividerModule,
      DialogModule,
      TagModule,
      TabMenuModule,
      ButtonModule,
      EntraSyncModule,
      VetcorPricingModule,
      DvmToolModule,
      GrowthToolkitModule,
      LeaderboardModule,
      MentoringAgreementModule,
      MessagingAppModule,
      RoleManagerModule,
      BrowserModule,
      MsalModule,
      LeaderToolkitModule,
   ],
   providers: [
      AuthManager,
      AuthApiClient,
      CallsApiClient,
      AuthGuard,
      HmRestrictionGuard,
      provideEnvironmentNgxMask(),
      { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: RulesEngineInterceptor, multi: true },
      EmployeeTypeMapApiClient,
      FilesApiClient,
      EscheatmentApiClient,
      ConfirmationService,
      PaymentTypeGroupMapClient,
      ProductsClient,
      DoctorScheduleClient,
      FeedbackService,
      FaxDataClient,
      AdminProductsClient,
      EntraSyncClient,
      PricingManagerClient,
      GrowthToolkitClient,
      LeaderboardClient,
      CreateZohoTicketForAppsClient,
      PracticesClient,
      MessagingClient,
      DVMPlanClient,
      RoleManagerClient,
      provideHttpClient(withInterceptorsFromDi()),
      provideAnimationsAsync(),
      providePrimeNG({
         theme: {
            preset: Lara,
            options: {
               darkModeSelector: false,
            },
         },
      }),
      provideHttpClient(withInterceptorsFromDi()),
      {
         provide: MSAL_INSTANCE,
         useFactory: MSALInstanceFactory,
      },
      MsalService,
   ],
})
export class AppModule {}
